import {Injectable} from '@angular/core';
import {CustomerId, DeviceSlotId, DeviceSlotTemplateId, MachineId, MachinePoolId} from '@webmate-web-core/shared/types/ids';
import {mergeMap, Observable, of, ReplaySubject} from 'rxjs';
import {Machine, MachinePool} from '@webmate-web-core/shared/types/devices/machine';
import {catchError, map} from 'rxjs/operators';
import {BillingInfo} from '@webmate-web-core/shared/services/admin.service';
import {WmAdminApi} from '@webmate-web-core/shared/api/rest/wm-admin.restapi';
import {DeviceSlot} from '@webmate-web-core/shared/types/devices/device-slot';
import {CustomerInfo} from '@webmate-web-core/shared/types/customer';
import {UserInfo} from '@webmate-web-core/shared/types/user/user-info';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {ProjectInfo} from '@webmate-web-core/shared/types/projectInfo';
import {DeviceSlotTemplate} from '@webmate-web-core/shared/types/devices/device-template';
import {Feature} from '@webmate-web-core/shared/types/feature';
import {User} from '@webmate-web-core/shared/types/user/user';
import {GenericApiResponse} from "@webmate-web-core/shared/api/apibase.api";
import {DeviceSlotHistoryEvent} from "@webmate-web-core/shared/types/interfaces/device-slot-history-event.interface";
import {Device} from "@webmate-web-core/shared/types/devices/device";
import {RefeedAuditsDTO, RefeedFindingsDTO, RefeedTestRunsDTO} from '@webmate-web-core/shared/types/refeed';

@Injectable({
    providedIn: 'root'
})
export class WmAdminService {

    private _customerSlotsCache: {[s: CustomerId]: ReplaySubject<DeviceSlot[]>} = {};

    private _customerSlotTemplate: {[s: CustomerId]: ReplaySubject<DeviceSlotTemplate[]>} = {};

    constructor(private wmAdminApi: WmAdminApi, private userService: UserService) {}

    searchUser(searchstring: string): Observable<User[]> {
        return this.wmAdminApi.searchUser(searchstring);
    }

    createCustomer(createCustomerDTO: any): Observable<any> {
        return this.wmAdminApi.createCustomer(createCustomerDTO);
    }

    deleteCustomer(customerId: CustomerId): Observable<GenericApiResponse> {
        return this.wmAdminApi.deleteCustomer(customerId);
    }

    getBillingInfoForCustomer(customerId: CustomerId, slotTemplateId: DeviceSlotTemplateId): Observable<BillingInfo[]> {
        return this.wmAdminApi.getBillingInfoForCustomer(customerId, slotTemplateId);
    }

    getMachinesForCustomer(customerId: CustomerId, poolId: MachinePoolId): Observable<Machine[]> {
        return this.wmAdminApi.getMachinesForCustomer(customerId, poolId);
    }

    getMachinePool(poolId: MachinePoolId): Observable<MachinePool> {
        return this.wmAdminApi.getPool(poolId);
    }

    getAssignedMachinesForCustomer(customerId: CustomerId): Observable<MachineId[]> {
        return this.wmAdminApi.getAssignedMachinesForCustomer(customerId);
    }

    getFeatureForCustomer(customerId: CustomerId, featureName: string): Observable<Feature> {
        return this.wmAdminApi.getFeaturesForCustomer(customerId).pipe(map(features => {
            return features[featureName];
        }))
    }

    getSlotsForCustomer(customerId: CustomerId, forceReload: boolean = false): Observable<DeviceSlot[]> {
        if (!forceReload && this._customerSlotsCache[customerId]) {
            return this._customerSlotsCache[customerId].asObservable();
        }
        return this.wmAdminApi.getSlotsForCustomer(customerId).pipe(mergeMap(slots => {
            if (!this._customerSlotsCache[customerId]) {
                this._customerSlotsCache[customerId] = new ReplaySubject(1);
            }
            this._customerSlotsCache[customerId].next(slots);
            return this._customerSlotsCache[customerId];
        }));
    }

    deleteSlot(slotId: DeviceSlotId, customerId: CustomerId): Observable<boolean> {
        return this.wmAdminApi.deleteSlot(slotId).pipe(mergeMap(success => {
            return this.getSlotsForCustomer(customerId, true);
        }), map(next => true), catchError(() => of(false)));
    }

    createSlot(customerId: CustomerId, slotInfo: any): Observable<any> {
        return this.wmAdminApi.createSlot(customerId, slotInfo);
    }
    updateSlot(customerId: CustomerId, slot: DeviceSlot): Observable<any> {
        return this.wmAdminApi.updateSlot(customerId, slot);
    }

    searchCustomer(searchstring: string): Observable<CustomerInfo[]> {
        return this.wmAdminApi.searchCustomer(searchstring);
    }

    getBillForCustomerAtTime(customerId: CustomerId, since: string, until: string): Observable<any> {
        return this.wmAdminApi.getBillForCustomerAtTime(customerId, since, until);
    }

    getProjectsForCustomer(customerId: CustomerId): Observable<ProjectInfo[]> {
        return this.wmAdminApi.getProjectsForCustomer(customerId);
    }

    getCustomer(customerId: CustomerId):Observable<CustomerInfo> {
        return this.wmAdminApi.getCustomer(customerId);
    }

    getSlotTemplatesForCustomer(customerId: CustomerId, forceReload: boolean = false): Observable<DeviceSlotTemplate[]> {
        if (!forceReload && this._customerSlotTemplate[customerId]) {
            return this._customerSlotTemplate[customerId].asObservable();
        }
        return this.wmAdminApi.getSlotTemplatessForCustomer(customerId).pipe(mergeMap(deployable => {
            if (!this._customerSlotTemplate[customerId]) {
                this._customerSlotTemplate[customerId] = new ReplaySubject(1);
            }
            this._customerSlotTemplate[customerId].next(deployable);
            return this._customerSlotTemplate[customerId];
        }));
    }

    getSlotTemplate(slotTemplateId: DeviceSlotTemplateId): Observable<DeviceSlotTemplate> {
        return this.wmAdminApi.getSlotTemplate(slotTemplateId);
    }

    getDeviceSlotHistory(slotId?: string, searchQuery?: string, startDate?: string, endDate?: string, historySearch?: boolean): Observable<DeviceSlotHistoryEvent[]> {
        return this.wmAdminApi.getDeviceSlotHistory(slotId, searchQuery, startDate, endDate, historySearch);
    }

    getAllDeployedDevices(): Observable<Device[]> {
        return this.wmAdminApi.getAllDeployedDevices();
    }

    clearSlotUpdatePending(slotId: DeviceSlotId): Observable<any> {
        return this.wmAdminApi.clearSlotUpdatePending(slotId);
    }

    refeedFindings(refeedDto: RefeedFindingsDTO): Observable<GenericApiResponse> {
        return this.wmAdminApi.refeedFindings(refeedDto);
    }

    refeedAudits(refeedDto: RefeedAuditsDTO): Observable<GenericApiResponse> {
        return this.wmAdminApi.refeedAudits(refeedDto);
    }

    refeedTestRuns(refeedDto: RefeedTestRunsDTO): Observable<GenericApiResponse> {
        return this.wmAdminApi.refeedTestRuns(refeedDto);
    }
}
